import React, {
  useEffect,
} from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { TextField } from '@waglabs/textfield';
import { Button } from '@waglabs/button-next';
import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import {
  ChevronLeftIcon,
} from '@waglabs/icons';
import { Typography } from '@waglabs/typography';

import { LockOutlinedIcon } from '../../../../../../icons';

import type { UseStepperReturnType } from '../../../../../../hooks/useStepper';
import {
  usePostAuthenticationVerifyUpgradeAndLinkSocial,
} from '../../../../../../hooks/api/usePostAuthenticationVerifyUpgradeAndLinkSocial';
import { useFacebookLogin } from '../../../../../../hooks';
import { useGoogleLogin } from '../../../../../../hooks/api/useGoogleLogin';

import {
  codeSchema,
  getFormikFieldHelper,
  pipe,
} from '../../../../../../utils';
import {
  Auth,
  DATA_TEST_IDS,
  MagicLoginLabels,
} from '../../../../../../constants';

type LoginByCodeFormStepProps = UseStepperReturnType & {};

const FormField = Object.freeze({
  Code: 'code',
});

const schema = Yup.object().shape({
  [FormField.Code]: codeSchema,
});

export const LoginByCodeFormStep = ({
  handleBack,
  handleNext,
  meta,
  setActiveStep,
}: LoginByCodeFormStepProps) => {
  const {
    execute: verifyUpgradeAndLinkSocialAuthentication,
    isSuccess: isVerifyUpgradeAndLinkSocialAuthenticationSuccess,
    error: verifyUpgradeAndLinkSocialAuthenticationError,
    errorMessage: verifyUpgradeAndLinkSocialAuthenticationErrorMessage,
    reset,
  } = usePostAuthenticationVerifyUpgradeAndLinkSocial({
    userRoleType: Auth.RoleType.Owner,
  });

  const {
    accessToken: facebookAccessToken,
  } = useFacebookLogin();

  const {
    googleIdToken,
  } = useGoogleLogin();

  const {
    errors,
    isValid,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleChange: handleFormikChange,
    handleBlur,
    setSubmitting,
  } = useFormik({
    isInitialValid: false,
    initialValues: schema.cast(undefined),
    validationSchema: schema,
    onSubmit: (formValues) => {
      verifyUpgradeAndLinkSocialAuthentication({
        // Make sure the code is string. The input field is in number format for UX
        code: String(formValues[FormField.Code]),
        // retrieved authentication method from previous step
        authenticationMethod: meta.authenticationMethod,
        facebookAccessToken,
        googleIdToken,
      });
    },
  });

  // Removes state error when the user update field text
  const handleChange = pipe(
    handleFormikChange,
    () => {
      if (!verifyUpgradeAndLinkSocialAuthenticationError) {
        return;
      }
      reset();
    },
  );

  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched,
  });

  const fieldError = (
    verifyUpgradeAndLinkSocialAuthenticationError || formikFieldHelper(FormField.Code).hasError
  );
  const fieldHelperText = (
    verifyUpgradeAndLinkSocialAuthenticationErrorMessage || formikFieldHelper(FormField.Code).errorMessage
  );

  useEffect(() => {
    // Reset on mount
    reset();
  }, [
    reset,
  ]);

  useEffect(() => {
    if (!isVerifyUpgradeAndLinkSocialAuthenticationSuccess) {
      return;
    }
    handleNext();
  }, [
    isVerifyUpgradeAndLinkSocialAuthenticationSuccess,
    handleNext,
  ]);

  useEffect(() => {
    if (!verifyUpgradeAndLinkSocialAuthenticationError) {
      return;
    }
    setSubmitting(false);
  }, [
    verifyUpgradeAndLinkSocialAuthenticationError,
    setSubmitting,
  ]);

  return (
    <Box>
      <Flex
        height="24px"
        alignItems="center"
        data-testid={DATA_TEST_IDS.LOGIN.loginByCodeForm.BackBtn}
        onClick={() => {
          /**
           * Prevent from going to step 2 if there's a 
           * `previousBackStep` in the `meta` because it 
           * is a signal that the previous step should be skipped
           * 
           * Check if `previousBackStep` is a number, then redirect
           * to that step instead of the previous step
           */
          if (meta && !isNaN(meta.previousBackStep)) {
            setActiveStep(meta.previousBackStep);
            return;
          }
          handleBack();
        }}
        hover
      >
        <ChevronLeftIcon
          color="wagGreen"
          cursor="pointer"
        />
        <Typography
          m={0}
          color="wagGreen"
          hover
        >
          Back
        </Typography>
      </Flex>
      <Typography
        px="24px"
        mt="16px"
        textAlign="center"
        data-testid={DATA_TEST_IDS.LOGIN.loginByCodeForm.InformativeLabel}
      >
        {MagicLoginLabels.LoginByCodeFormStep[meta.authenticationMethod]
          || MagicLoginLabels.LoginByCodeFormStep[Auth.AllowedMethods.CodeEmail]
        }
      </Typography>
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: '24px',
        }}
      >
        <TextField
          label="Code"
          name={FormField.Code}
          inputProps={{
            fontFamily: 'muli',
            startAdornment: () => (
              <LockOutlinedIcon />
            ),
          }}
          containerProps={{
            backgroundColor: 'white',
            // Due to nature of this page having a grey background,
            // we need to update the backgroundColor. Having said that,
            // the Textfield component thought the user is manually handling it
            // that's why we have to manually set the borderColor
            // TODO - Update this on @waglabs/textfield
            borderColor: fieldError ? 'red' : 'white',
          }}
          error={fieldError}
          helperText={fieldHelperText}
          value={values[FormField.Code]}
          onChange={handleChange}
          onBlur={handleBlur}
          data-testId={DATA_TEST_IDS.LOGIN.loginByCodeForm.Code}
          fullWidth
        />
        <Button
          variant="contained"
          type="submit"
          disabled={!isValid || isSubmitting}
          mt="12px"
          mb="24px"
          size="medium"
          data-testId={DATA_TEST_IDS.LOGIN.loginByCodeForm.SignInBtn}
        >
          Sign in
        </Button>
      </form>
    </Box>
  );
};
